import React, { useMemo } from 'react';
import GridItem from '../../../layout/GridComponents/GridItem';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useNavReconPositions } from '../../../../api/rs-hg/nav-recon/hooks/queries';
import {
  NavReconPosition,
  NavReconPositionTotals,
} from '../../../../api/rs-hg/nav-recon/types';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import CustomTable from '../../../tables/CustomTable';
import {
  NavReconPositionTableRow,
  getNavReconPositionsColumns,
} from './navReconPositionsColumns';
import { NavReconPositionThresholds } from '../../../../api/rs-hg/nav-recon/types';
import { NAV_RECON_NO_POSITION_THRESHOLDS } from './navReconGapStatus';
import NavReconPositionsDetailPanel from './NavReconPositionsDetailPanel.component';
import NavReconPositionsStats from './NavReconPositionsStats';
import { useNavReconPositionsTableLayoutStyles } from './navReconPositionsTableLayoutStyles';
import { mainColors } from '../../../../styling/theme';

const TABLE_HEIGHT = 'calc(100vh - 15.62rem)';
const AGGREGATE_ROW_LABEL = 'Aggregate';

type NavReconPositionsFundMeta = {
  fund_currency: string;
  threshold: NavReconPositionThresholds;
};

/** Object payload so QueryBoundary does not treat an empty row list as missing data. */
type NavReconPositionsLoaded = {
  rows: NavReconPositionTableRow[];
  fundMeta?: NavReconPositionsFundMeta;
};

const totalsToRowMetrics = (
  totals: NavReconPositionTotals | undefined,
): Pick<
  NavReconPositionTableRow,
  'caceis_p_and_l_perc' | 'six_p_and_l_perc' | 'six_caceis_gap_perc'
> => ({
  caceis_p_and_l_perc: totals?.caceis_p_and_l_perc,
  six_p_and_l_perc: totals?.six_p_and_l_perc,
  six_caceis_gap_perc: totals?.six_caceis_gap_perc,
});

const getAllPositions = (
  positionsByAssetClass: Record<string, NavReconPosition[]> | undefined,
): NavReconPosition[] =>
  Object.values(positionsByAssetClass ?? {}).flat();

const buildAssetClassRows = (
  overallTotals: NavReconPositionTotals | undefined,
  assetClassTotals: Record<string, NavReconPositionTotals> | undefined,
  positionsByAssetClass: Record<string, NavReconPosition[]> | undefined,
): NavReconPositionTableRow[] => {
  const assetClasses = Object.keys(assetClassTotals ?? {}).sort((a, b) =>
    a.localeCompare(b),
  );

  const aggregatePositions = getAllPositions(positionsByAssetClass);

  return [
    {
      asset_class: AGGREGATE_ROW_LABEL,
      isAggregateRow: true,
      positions: aggregatePositions,
      position_count: aggregatePositions.length,
      ...totalsToRowMetrics(overallTotals),
    },
    ...assetClasses.map((assetClass) => {
      const positions = positionsByAssetClass?.[assetClass] ?? [];
      return {
        asset_class: assetClass,
        positions,
        position_count: positions.length,
        ...totalsToRowMetrics(assetClassTotals?.[assetClass]),
      };
    }),
  ];
};

/**
 * Fund-level NAV-Recon positions for the selected fund and date.
 */
const NavReconPositionsPage: React.FC<FundInfoComponentProps> = ({
  positionDate,
  fundId,
}) => {
  const positionsQuery = useNavReconPositions(positionDate, fundId);

  const { rows, fundMeta } = useMemo(() => {
    const payload = positionsQuery.data;
    if (!payload?.length) {
      return {
        rows: [] as NavReconPositionTableRow[],
        fundMeta: undefined as NavReconPositionsFundMeta | undefined,
      };
    }
    const block = payload.find((b) => b.fund_name === fundId) ?? payload[0];
    return {
      rows: buildAssetClassRows(
        block?.overall_totals,
        block?.asset_class_totals,
        block?.positions_by_asset_class,
      ),
      fundMeta: block
        ? {
            fund_currency: block.fund_currency,
            threshold: block.threshold,
          }
        : undefined,
    };
  }, [positionsQuery.data, fundId]);

  const tableLayoutClasses = useNavReconPositionsTableLayoutStyles();
  const columns = useMemo(
    () =>
      fundMeta
        ? getNavReconPositionsColumns(fundMeta.threshold)
        : getNavReconPositionsColumns(NAV_RECON_NO_POSITION_THRESHOLDS),
    [fundMeta],
  );

  const { csvFields, fieldsMap } = useMemo(() => {
    const exportable = columns.filter((col) => col.field);
    return {
      csvFields: exportable.map((col) => col.field as string),
      fieldsMap: exportable
        .filter((col) => col.title)
        .map((col) => ({
          key: col.field as string,
          label: col.title as string,
        })),
    };
  }, [columns]);

  if (!fundId) {
    return (
      <GridItem card xs={12}>
        <NoDataMessage message="Select a fund to load positions." />
      </GridItem>
    );
  }

  const loaded: NavReconPositionsLoaded | undefined =
    positionsQuery.data ? { rows, fundMeta } : undefined;

  return (
    <QueryBoundary<NavReconPositionsLoaded>
      isLoading={positionsQuery.isLoading}
      isError={positionsQuery.isError}
      error={positionsQuery.error}
      data={loaded}
      loadingMessages={['Loading positions…']}
      errorMessage="Failed to load positions."
      noDataMessage="No position data for the selected fund and date."
    >
      {(data) => (
        <GridItem card xs={12}>
          <CustomTable<NavReconPositionTableRow>
            id="nav-recon-positions"
            csvFields={csvFields}
            fieldsMap={fieldsMap}
            showToolbar
            loadingHeight={TABLE_HEIGHT}
            toolbarComponents={{
              toolbarTitle: (
                <NavReconPositionsStats
                  title="Positions"
                  fundCurrency={data.fundMeta?.fund_currency}
                  threshold={data.fundMeta?.threshold}
                />
              ),
              titleStyle: {
                fontWeight: 'inherit',
                fontSize: 'inherit',
                width: '100%',
              },
            }}
            columns={columns}
            data={data.rows}
            tableRootStyles={tableLayoutClasses.tableRoot}
            onRowClick={(_event, _rowData, togglePanel) => togglePanel?.()}
            detailPanel={(rowData: NavReconPositionTableRow) =>
              fundMeta ? (
                <NavReconPositionsDetailPanel
                  rowData={rowData}
                  thresholds={fundMeta.threshold}
                />
              ) : null
            }
            options={{
              paging: false,
              pageSize: Math.max(10, data.rows.length),
              pageSizeOptions: [10, 25, 50, data.rows.length || 10],
              search: true,
              exportButton: true,
              rowStyle: (rowData: NavReconPositionTableRow) => ({
                padding: '0.62rem',
                backgroundColor: rowData.isAggregateRow
                  ? mainColors.lightGrey
                  : 'white',
              }),
              raptorExport: true,
              detailPanelColumnStyle: {
                width: '3rem',
                maxWidth: '3rem',
                minWidth: '3rem',
              },
            }}
          />
        </GridItem>
      )}
    </QueryBoundary>
  );
};

export default NavReconPositionsPage;
